import type { Timestamp } from 'firebase/firestore';
import type { SharedUser, SharePermission, InviteStatus } from './thinkspace';

// Re-export shared types for convenience
export type { SharedUser, SharePermission, InviteStatus };

// Paper sizes for printing
export type PaperSize = 'a4' | 'letter';

// Page template types
export type PageTemplate =
  | 'blank'
  | 'ruled-college'
  | 'ruled-wide'
  | 'grid-small'
  | 'grid-large'
  | 'dotted';

// Paper dimensions in mm (for PDF export)
export const PAPER_DIMENSIONS: Record<PaperSize, { width: number; height: number }> = {
  a4: { width: 210, height: 297 },
  letter: { width: 216, height: 279 }, // 8.5 x 11 inches
};

// Paper dimensions in pixels at 96 DPI (for canvas rendering)
export const PAPER_PIXELS: Record<PaperSize, { width: number; height: number }> = {
  a4: { width: 794, height: 1123 }, // 210mm @ 96dpi
  letter: { width: 816, height: 1056 }, // 8.5x11in @ 96dpi
};

// Template configuration for rendering backgrounds
export type TemplateType = 'none' | 'lines' | 'grid' | 'dots';

export interface TemplateConfig {
  type: TemplateType;
  spacing: number; // in pixels
  color: string;
}

export const TEMPLATE_CONFIG: Record<PageTemplate, TemplateConfig> = {
  blank: { type: 'none', spacing: 0, color: 'transparent' },
  'ruled-college': { type: 'lines', spacing: 26, color: '#d0d0d0' }, // ~7mm
  'ruled-wide': { type: 'lines', spacing: 33, color: '#d0d0d0' }, // ~8.7mm
  'grid-small': { type: 'grid', spacing: 19, color: '#e0e0e0' }, // ~5mm
  'grid-large': { type: 'grid', spacing: 38, color: '#e0e0e0' }, // ~10mm
  dotted: { type: 'dots', spacing: 26, color: '#c0c0c0' },
};

// Human-readable template names
export const TEMPLATE_NAMES: Record<PageTemplate, string> = {
  blank: 'Blank',
  'ruled-college': 'Ruled (College)',
  'ruled-wide': 'Ruled (Wide)',
  'grid-small': 'Grid (Small)',
  'grid-large': 'Grid (Large)',
  dotted: 'Dot Grid',
};

// Human-readable paper size names
export const PAPER_SIZE_NAMES: Record<PaperSize, string> = {
  a4: 'A4 (210 x 297 mm)',
  letter: 'US Letter (8.5 x 11 in)',
};

// Individual notebook page
export interface NotebookPage {
  id: string;
  order: number;
  template: PageTemplate;
  // Canvas content stored as JSON (Fabric.js canvas data)
  canvasData: string;
  // Thumbnail for page preview (base64 PNG, small resolution)
  thumbnail: string | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Main Notebook document
export interface Notebook {
  id: string;
  name: string;
  description: string;
  ownerId: string;
  ownerEmail: string;
  ownerName: string;
  // Default settings for new pages
  paperSize: PaperSize;
  defaultTemplate: PageTemplate;
  // Pages array (embedded document for simplicity)
  pages: NotebookPage[];
  // Sharing - view only for notebooks (no write permission)
  sharedWith: SharedUser[];
  // Cover image (first page thumbnail or custom)
  coverImage: string | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Input types for creating/updating
export interface CreateNotebookInput {
  name: string;
  description?: string;
  paperSize?: PaperSize;
  defaultTemplate?: PageTemplate;
}

export interface UpdateNotebookInput {
  name?: string;
  description?: string;
  paperSize?: PaperSize;
  defaultTemplate?: PageTemplate;
  coverImage?: string | null;
}

export interface CreatePageInput {
  template?: PageTemplate;
  insertAfterPageId?: string; // Insert after specific page, or at end if not specified
}

export interface UpdatePageInput {
  canvasData?: string;
  thumbnail?: string | null;
  template?: PageTemplate;
}

// For sharing (view-only for notebooks)
export interface InviteToNotebookInput {
  email: string;
  // Note: Notebooks only support 'read' permission
}

// Helper functions
export const getPageCount = (notebook: Notebook): number => notebook.pages.length;

export const sortPagesByOrder = (pages: NotebookPage[]): NotebookPage[] =>
  [...pages].sort((a, b) => a.order - b.order);

export const getPageByOrder = (notebook: Notebook, order: number): NotebookPage | undefined =>
  notebook.pages.find((p) => p.order === order);

export const getPageById = (notebook: Notebook, pageId: string): NotebookPage | undefined =>
  notebook.pages.find((p) => p.id === pageId);

// Get next page order number
export const getNextPageOrder = (notebook: Notebook): number => {
  if (notebook.pages.length === 0) return 0;
  return Math.max(...notebook.pages.map((p) => p.order)) + 1;
};

// Format page number for display (1-indexed)
export const formatPageNumber = (order: number): string => `Page ${order + 1}`;
